import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const turnstileWidgetId = useRef(null);
  const turnstileRef = useRef(null);

  // Get Cloudflare site key from environment variables
  const CLOUDFLARE_SITE_KEY = process.env.REACT_APP_CLOUDFLARE_SITE_KEY;

  // Flag to prevent duplicate initializations
  const initialized = useRef(false);

  // Define the development mode check once
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

  // Initialize Turnstile when component mounts
  useEffect(() => {
    // Skip in StrictMode double-render
    if (initialized.current) return;
    initialized.current = true;

    // For development environments, set a bypass token
    if (isDevelopment) {
      console.log('Development mode: Setting mock Turnstile token');
      setTurnstileToken('dev-token');
      return; // Skip Turnstile in development
    }

    // If no site key is provided, mark error and skip Turnstile
    if (!CLOUDFLARE_SITE_KEY) {
      console.error('No Cloudflare Turnstile site key provided in environment variables');
      setTurnstileError(true);
      return;
    }

    // Set up callback for when Turnstile loads
    window.onloadTurnstileCallback = () => {
      if (!window.turnstile || !turnstileRef.current) return;

      try {
        // Only render if no widget ID exists yet
        if (!turnstileWidgetId.current) {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: CLOUDFLARE_SITE_KEY,
            callback: (token) => {
              console.log('Turnstile verification successful');
              setTurnstileToken(token);
            },
            'error-callback': () => {
              console.error('Turnstile verification failed');
              setTurnstileError(true);
            }
          });
        }
      } catch (err) {
        console.error('Error rendering Turnstile widget:', err);
        setTurnstileError(true);
      }
    };

    // Check if Turnstile is already loaded
    if (window.turnstile && turnstileRef.current) {
      window.onloadTurnstileCallback();
    }

    // Cleanup on unmount
    return () => {
      if (window.turnstile && turnstileWidgetId.current) {
        try {
          window.turnstile.reset(turnstileWidgetId.current);
        } catch (error) {
          console.warn('Error during Turnstile cleanup:', error);
        }
      }
    };
  }, [CLOUDFLARE_SITE_KEY, isDevelopment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);

    // Require token in production when site key is configured
    if (!turnstileToken && !isDevelopment && CLOUDFLARE_SITE_KEY) {
      setSubmitStatus({
        type: 'danger',
        message: 'Please complete the security verification.'
      });
      setLoading(false);
      return;
    }

    try {
      // API endpoint - fallback to local development endpoint if not set
      const API_URL = process.env.REACT_APP_API_URL || '/api/contact';

      // For local development, simulate a successful response if no API URL
      if (isDevelopment && !process.env.REACT_APP_API_URL) {
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 800));

        // Simulate successful response
        setSubmitStatus({
          type: 'success',
          message: 'Development mode: Message submission simulated.'
        });

        // Mark form as successfully submitted
        setFormSubmitted(true);

        // Reset form fields
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });

        setLoading(false);
        return;
      }

      // Real API call
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          token: turnstileToken || 'dev-token'
        }),
      });

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const result = await response.json();

      setSubmitStatus({
        type: 'success',
        message: result.message || 'Thank you for your message. We will get back to you soon!',
      });
      // Mark form as successfully submitted
      setFormSubmitted(true);

      // Reset form fields on success
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      // Reset Turnstile after a short delay to allow user to see success message
      if (!isDevelopment && CLOUDFLARE_SITE_KEY && window.turnstile && turnstileWidgetId.current) {
        setTimeout(() => {
          try {
            window.turnstile.reset(turnstileWidgetId.current);
            setTurnstileToken(null);
          } catch (error) {
            console.warn('Could not reset Turnstile:', error);
          }
        }, 2000); // Wait 2 seconds before resetting
      }
    } catch (error) {
      console.error('Error submitting form:', error);

      // Special handling for local development
      if (isDevelopment) {
        setSubmitStatus({
          type: 'warning',
          message: 'Development mode: API endpoint not available. Form submission simulated.'
        });
        setFormSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus({
          type: 'danger',
          message: 'There was a network error. Please try again later.',
        });
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="contact-form">
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="name">
              <Form.Label>Your Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your name"
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="email">
              <Form.Label>Your Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="phone">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="subject">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Enter message subject"
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group controlId="message" className="mb-4">
          <Form.Label>Message <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            placeholder="Enter your message"
          />
        </Form.Group>

        <div className="turnstile-container mb-4">
          {isDevelopment ? (
            <div className="text-center mb-2">
              <Alert variant="info" className="py-2">
                <small>Development mode: Turnstile verification bypassed</small>
              </Alert>
            </div>
          ) : (
            <>
              {turnstileToken && (
                <div className="turnstile-success text-center mb-2">
                  <Alert variant="success" className="py-2">
                    <span className="d-flex align-items-center justify-content-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check-circle-fill me-2" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      </svg>
                      Verification complete
                    </span>
                  </Alert>
                </div>
              )}
              {turnstileError && !turnstileToken && (
                <div className="turnstile-error text-center mb-2">
                  <Alert variant="warning" className="py-2">
                    <small>Verification failed. Please try again or refresh the page.</small>
                  </Alert>
                </div>
              )}
              {!turnstileToken && (
                <div className="turnstile-success text-center mb-2">
                  <div ref={turnstileRef} className="d-flex justify-content-center"></div>
                </div>
              )}
            </>
          )}
        </div>

        {!formSubmitted && (
          <Button
            variant="primary"
            type="submit"
            disabled={loading || (!turnstileToken && !isDevelopment && CLOUDFLARE_SITE_KEY)}
            className="w-100"
            style={{ borderRadius: '6px', padding: '12px' }}
          >
            {loading ? 'Sending...' :
             isDevelopment ? 'Submit Message (Dev Mode)' :
             !CLOUDFLARE_SITE_KEY ? 'Submit Message (No Verification)' :
             turnstileToken ? 'Submit Message' : 'Please Complete Verification'}
          </Button>
        )}

        {submitStatus && (
          <Alert variant={submitStatus.type} className="mt-3">
            {submitStatus.message}
          </Alert>
        )}

      </Form>
    </div>
  );
};

export default ContactForm;
